/**
 * AI Best Match — button that asks GPT-4o vision to pick top 3 products for the user.
 * Parent controls the highlighted product IDs via `onPicks(ids)` callback.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Target, Loader2 } from 'lucide-react';
import api from '../../utils/api';

const AIBestMatchButton = ({ canvasRef, category, onPicks, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [overall, setOverall] = useState('');

  const askBestMatch = async () => {
    if (!canvasRef?.current) return toast.error('Canvas not ready');
    setLoading(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.7);
      const r = await api.post('/trial-room/best-match', { image_base64: dataUrl, category });
      const picks = r.data.picks || [];
      if (picks.length === 0) {
        toast.error('AI could not pick — try clearer photo or add more products in admin');
        onPicks?.([], []);
      } else {
        toast.success(`✨ AI picked ${picks.length} best matches for you!`);
        setOverall(r.data.overall || '');
        onPicks?.(picks.map((p) => p.id), picks);
      }
    } catch (e) {
      toast.error(`❌ ${e.response?.data?.detail || 'Best match failed'}`);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={askBestMatch}
        disabled={loading}
        data-testid="ai-bestmatch-btn"
        className={`px-3 py-1 rounded-lg bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 text-black text-[11px] font-black flex items-center gap-1 hover:brightness-110 disabled:opacity-50 ${className}`}>
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />}
        {loading ? 'Analyzing...' : '🎯 AI Picks for Me'}
      </button>
      {overall && <span className="text-[10px] text-amber-200 italic">{overall}</span>}
    </div>
  );
};

export default AIBestMatchButton;
